import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import TextField from "@/common/components/TextField";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { ShowHeader } from "@/common/components/ShowHeader";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import SelectController from "@/common/components/SelectController";
import TextArea from "@/common/components/TextArea";
import useMaintenanceWorkflowSchedule from "../../hooks/useWorkflowSchedules";
import { createWorkflowSchedulesSchema, CreateWorkflowSchedulesSchema } from "../../services/workflowSchedules.schema";
import { MaintenanceWorkflowSchedulesTransformer } from "../../services/maintenanceWorkflowSchedulesTransformer";
import { ModalDeleteMaintenanceWorkflowSchedule } from "../../components/ModalDelete";
import AccordionMaintenanceWorkflowSchedulesOverview from "../../components/AccordionOverview";
import AccordionMaintenanceWorkflowSchedulesEquipments from "../../components/AccordionEquipments";
import AccordionMaintenanceWorkflowSchedulesExecution from "../../components/AccordionExecution";
import AccordionMaintenanceWorkflowSchedulesObjectives from "../../components/AccordionObjectives";
import { maintenanceWorkflowSchedulesKindOptions } from "../../constants/kind.constant";

const Show = () => {
  const { id } = useParams();

  const navigate = useNavigate();

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const {
    data: workflowSchedule,
    isLoading: workflowScheduleIsLoading,
    error: workflowScheduleError = {} as any,
    refetch: refetchWorkflowSchedule
  } = useMaintenanceWorkflowSchedule(String(id))

  const {
    register,
    control,
    reset,
    formState: { errors }
  } = useForm<CreateWorkflowSchedulesSchema>({
    resolver: zodResolver(createWorkflowSchedulesSchema)
  });

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const userHasPermissionToSeeMaintenanceWorkflowSchedule =
    isUserPermissionsPending || hasUserPermission('Client::Maintenance::WorkflowSchedules::Show')

  useEffect(() => {
    if (!workflowScheduleIsLoading && workflowSchedule) {
      const formData = MaintenanceWorkflowSchedulesTransformer.formDataTransformer(workflowSchedule)
      reset(formData)
    }
  }, [workflowScheduleIsLoading])

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]">
      <ShowHeader
        goBack
        goBackProps={{ 
          fallback: '/maintenance-workflows-schedules',
          searchLastRouteFromFallback: true
        }}
        title="Programação de Manutenção"
        subtitle={workflowSchedule?.display_name}
        buttons={
          userHasPermissionToSeeMaintenanceWorkflowSchedule ? (
            <div className="flex items-center gap-4">
              <ModalDeleteMaintenanceWorkflowSchedule workflowScheduleId={id} />
              <Button
                operationKlassToCheckPermission="Client::Maintenance::WorkflowSchedules::Update"
                onClick={() => navigate('edit')}
              >
                Editar
              </Button>
            </div>
          ) : undefined
        }
      />

      <ErrorMessageWrapper 
        statusCode={workflowScheduleError?.status}
        refetchFunction={refetchWorkflowSchedule}
      >
        <Box className="flex flex-col flex-1 overflow-auto gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
          <AccordionMaintenanceWorkflowSchedulesOverview />

          <div className="flex gap-2">
            <TextField
              label="Nome"
              {...register('display_name')}
              className="flex-1"
              maxLength={32}
              disabled
            />

            <SelectController
              control={control}
              name="kind"
              label="Tipo de manutenção"
              className="flex-1"
              options={maintenanceWorkflowSchedulesKindOptions}
              isDisabled
            />
          </div>

          <TextArea
            label="Descrição (será exibida na solicitação de manutenção)"
            {...register('observations')}
            rows={6}
            disabled
          />

          <AccordionMaintenanceWorkflowSchedulesEquipments
            control={control}
            errors={errors}
            disabled
          />

          <AccordionMaintenanceWorkflowSchedulesExecution
            control={control}
            errors={errors}
            disabled
          />

          <AccordionMaintenanceWorkflowSchedulesObjectives
            control={control}
            errors={errors}
            objectives={workflowSchedule?.objectives}
            disabled
          />
        </Box>

        {messages && (
          <ModalMessageSimple
            messages={messages}
            setMessages={setMessages}
            isError
          />
        )}
      </ErrorMessageWrapper>
    </div>
  );
};

export default Show;
